import { useEffect, useId, useRef } from 'react'
import { Link } from 'react-router'
import { LoaderCircle } from 'lucide-react'
import { Alert } from '../common/Alert'
import styles from './AuthFormCard.module.css'

export function AuthFormCard({
  title,
  subtitle,
  helperText,
  errorMessage,
  successMessage,
  onSubmit,
  footer,
  children,
}) {
  const errorSummaryRef = useRef(null)
  const errorId = useId()

  useEffect(() => {
    if (errorMessage) {
      errorSummaryRef.current?.focus()
    }
  }, [errorMessage])

  return (
    <div className={styles.card}>
      {(title || subtitle) && (
        <div className={styles.header}>
          <p className={styles.eyebrow}>Wager Studio</p>
          {title && <h1 className={styles.title}>{title}</h1>}
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </div>
      )}

      {helperText && (
        <div className={styles.contextRow}>
          <span className={styles.helperText}>{helperText}</span>
        </div>
      )}

      <form onSubmit={onSubmit} aria-describedby={errorMessage ? errorId : undefined}>
        {errorMessage && (
          <div ref={errorSummaryRef} tabIndex={-1} className={styles.errorSummary}>
            <Alert variant="error">
              <span id={errorId}>{errorMessage}</span>
            </Alert>
          </div>
        )}
        {successMessage && <Alert variant="success">{successMessage}</Alert>}
        {children}
      </form>

      {footer && <div className={styles.footer}>{footer}</div>}
    </div>
  )
}

export function AuthFooterLink({ to, children }) {
  return (
    <div className={styles.supportRow}>
      <Link to={to} className={styles.textLink}>
        {children}
      </Link>
    </div>
  )
}

export function AuthField({
  id,
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  required = false,
  disabled = false,
  autoComplete,
  autoFocus = false,
  inputClassName,
  inputRef,
  errorMessage,
  describedBy,
  ...rest
}) {
  const generatedId = useId()
  const inputId = id || `auth-field-${generatedId}`
  const errorId = `${inputId}-error`
  const descriptionIds = [describedBy, errorMessage ? errorId : null].filter(Boolean).join(' ') || undefined

  return (
    <div className={styles.formGroup}>
      {label && <label htmlFor={inputId}>{label}</label>}
      <input
        ref={inputRef}
        id={inputId}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        className={[styles.input, inputClassName].filter(Boolean).join(' ')}
        aria-invalid={errorMessage ? 'true' : undefined}
        aria-describedby={descriptionIds}
        {...rest}
      />
      {errorMessage && <p id={errorId} className={styles.fieldError}>{errorMessage}</p>}
    </div>
  )
}

export function AuthSubmitButton({
  loading = false,
  submitted = false,
  disabled = false,
  loadingLabel,
  children,
}) {
  const isDisabled = disabled || loading || submitted

  return (
    <button
      type="submit"
      className={styles.submitButton}
      disabled={isDisabled}
      aria-busy={loading || undefined}
    >
      {loading && <LoaderCircle size={17} className={styles.loadingIcon} aria-hidden="true" />}
      <span>{children}</span>
      {loading && loadingLabel && (
        <span className={styles.visuallyHidden} role="status">{loadingLabel}</span>
      )}
    </button>
  )
}
